import PropTypes from 'prop-types';
import merge from 'lodash/merge';
import {useMemo} from 'react';
// @mui
import CssBaseline from '@mui/material/CssBaseline';
import {createTheme, ThemeProvider as MuiThemeProvider} from '@mui/material/styles';
// emotion
import {CacheProvider} from '@emotion/react';
// components
import {useSettingsContext} from 'packages/common/components/settings';
// system
import {palette} from './palette';
import {shadows} from './shadows';
import {typography} from './typography';
import {customShadows} from './custom-shadows';
import {componentsOverrides} from './overrides';
// options
import {presets} from './options/presets';
import {darkMode} from './options/dark-mode';
import {contrast} from './options/contrast';
import RTL, {direction} from './options/right-to-left';

// Export intensities system
export * from './intensities';

// ----------------------------------------------------------------------

export default function ThemeProvider({children, emotionCache}) {
  const settings = useSettingsContext();

  const darkModeOption = darkMode(settings.themeMode);

  const presetsOption = presets(settings.themeColorPresets);

  const contrastOption = contrast(settings.themeContrast === 'bold', settings.themeMode);

  const directionOption = direction(settings.themeDirection);

  const baseOption = useMemo(
      () => ({
        palette: palette('light'),
        shadows: shadows('light'),
        customShadows: customShadows('light'),
        typography,
        shape: {borderRadius: 8},
        breakpoints: {
          values: {
            xs: 0,
            sm: 600,
            md: 900,
            lg: 1322,
            xl: 1536,
          },
        },
      }),
      [],
  );

  const memoizedValue = useMemo(
      () =>
        merge(
            baseOption,
            directionOption,
            darkModeOption,
            presetsOption,
            contrastOption.theme,
        ),
      [baseOption, directionOption, darkModeOption, presetsOption, contrastOption.theme],
  );

  const theme = createTheme(memoizedValue);

  theme.components = merge(componentsOverrides(theme), contrastOption.components);

  const content = (
    <MuiThemeProvider theme={theme}>
      <RTL themeDirection={settings.themeDirection}>
        <CssBaseline />
        {children}
      </RTL>
    </MuiThemeProvider>
  );

  return emotionCache ? (
    <CacheProvider value={emotionCache}>{content}</CacheProvider>
  ) : (
    content
  );
}

ThemeProvider.propTypes = {
  children: PropTypes.node,
  emotionCache: PropTypes.any,
};

// Export individual color constants for direct import
export {
  NAVY,
  PURPLE,
  PINK,
  TEAL,
  INDIGO,
  SLATE,
  AMBER,
  CYAN,
  SKY,
  BLUE,
  ROSE,
  GREY,
  PRIMARY,
  SECONDARY,
  INFO,
  SUCCESS,
  WARNING,
  ERROR,
} from './palette';
